import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react'

type ThemeMode = 'light' | 'dark'
// 'system' follows the OS appearance live (and is the default when the user
// has never made an explicit choice).
export type ThemePreference = 'light' | 'dark' | 'system'

interface ThemeContextType {
  mode: ThemeMode
  preference: ThemePreference
  setPreference: (preference: ThemePreference) => void
  toggleTheme: () => void
  setMode: (mode: ThemeMode) => void
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

// New key on purpose: the old 'theme' key was auto-written on every visit, so
// it can't be trusted as an explicit user choice and would block OS-following.
const THEME_STORAGE_KEY = 'theme-preference'
const LEGACY_THEME_STORAGE_KEY = 'theme'

const getSystemMode = (): ThemeMode =>
  window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'

const getSavedPreference = (): ThemePreference | null => {
  const saved = localStorage.getItem(THEME_STORAGE_KEY)
  return saved === 'light' || saved === 'dark' || saved === 'system'
    ? saved
    : null
}

export function ThemeContextProvider({ children }: { children: ReactNode }) {
  // No explicit choice yet (logged out / first visit) -> follow the OS setting,
  // same as an explicit 'system' preference.
  const [preference, setPreferenceState] = useState<ThemePreference>(() => {
    localStorage.removeItem(LEGACY_THEME_STORAGE_KEY)
    return getSavedPreference() ?? 'system'
  })
  const [systemMode, setSystemMode] = useState<ThemeMode>(getSystemMode)

  // Track live OS theme changes so 'system' flips with the device.
  useEffect(() => {
    const media = window.matchMedia?.('(prefers-color-scheme: dark)')
    if (!media) return

    const onChange = (event: MediaQueryListEvent) => {
      setSystemMode(event.matches ? 'dark' : 'light')
    }

    media.addEventListener('change', onChange)
    return () => media.removeEventListener('change', onChange)
  }, [])

  const mode: ThemeMode = preference === 'system' ? systemMode : preference

  const setPreference = (next: ThemePreference) => {
    localStorage.setItem(THEME_STORAGE_KEY, next)
    setPreferenceState(next)
  }

  // Kept for callers that think in resolved modes (account theme sync).
  const setMode = (next: ThemeMode) => {
    setPreference(next)
  }

  const toggleTheme = () => {
    setPreference(mode === 'dark' ? 'light' : 'dark')
  }

  return (
    <ThemeContext.Provider
      value={{ mode, preference, setPreference, toggleTheme, setMode }}
    >
      {children}
    </ThemeContext.Provider>
  )
}

export function useThemeMode() {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useThemeMode must be used within ThemeContextProvider')
  }
  return context
}
